import { useState, useRef } from 'react';
import { useFixedCosts, useSalaries } from '../../hooks/useData';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Trash2, Wrench, AlertTriangle, Upload, Loader2, Check, ArrowUp, ArrowDown, FileText, X, Activity, Zap } from 'lucide-react';
import { toast } from 'sonner';

const PatronFixes = () => {
  const { costs, loading, refetch } = useFixedCosts();
  const { salaries } = useSalaries();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCost, setNewCost] = useState({ categorie: '', libelle: '', montant: 0 });
  const [releveData, setReleveData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selections, setSelections] = useState({});
  const fileRef = useRef(null);

  const handleAdd = async () => {
    try {
      await api.post('/fixed-costs', newCost);
      toast.success('Charge ajoutee');
      setShowAddDialog(false);
      setNewCost({ categorie: '', libelle: '', montant: 0 });
      refetch();
    } catch { toast.error("Erreur lors de l'ajout"); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/fixed-costs/${id}`);
      toast.success('Charge supprimee');
      refetch();
    } catch { toast.error('Erreur'); }
  };

  const handleUploadReleve = async (file) => {
    if (!file) return;
    setUploading(true);
    setReleveData(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/analyze-releve', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setReleveData(res.data);
      const sel = {};
      res.data.charges.forEach((c, i) => {
        if (c.status === 'nouveau') sel[i] = 'add';
        else if (c.status === 'hausse' || c.status === 'baisse') sel[i] = 'update';
        else sel[i] = 'skip';
      });
      setSelections(sel);
      toast.success(`Releve analyse: ${res.data.charges.length} charges trouvees`);
    } catch { toast.error("Erreur analyse du releve"); }
    setUploading(false);
  };

  const handleApplyReleve = async () => {
    if (!releveData) return;
    setApplying(true);
    try {
      const updates = releveData.charges.map((c, i) => ({ ...c, action: selections[i] || 'skip' }));
      const res = await api.post('/apply-releve', { updates });
      toast.success(res.data.message);
      setReleveData(null);
      setSelections({});
      refetch();
    } catch { toast.error("Erreur application"); }
    setApplying(false);
  };

  const handleAutoApplyReleve = async () => {
    if (!releveData) return;
    setApplying(true);
    try {
      const res = await api.post('/releve/auto-apply', { charges: releveData.charges });
      toast.success(`${res.data.message} — Alertes envoyees sur Telegram`);
      setReleveData(null);
      setSelections({});
      refetch();
    } catch { toast.error("Erreur application auto"); }
    setApplying(false);
  };

  const groupedCosts = costs.reduce((acc, cost) => {
    if (!acc[cost.categorie]) acc[cost.categorie] = [];
    acc[cost.categorie].push(cost);
    return acc;
  }, {});

  const totalFixes = costs.reduce((sum, c) => sum + (c.montant || 0), 0);
  const totalSalaires = salaries.reduce((sum, s) => sum + (s.salaire_net || 0) * (1 + (s.taux_charges || 42) / 100), 0);
  const seuilSurvie = totalFixes + totalSalaires;
  const categories = Object.keys(groupedCosts).sort();

  const statusIcon = (status) => {
    switch (status) {
      case 'nouveau': return <Plus size={14} style={{ color: 'rgb(var(--lm-green))' }} />;
      case 'hausse': return <ArrowUp size={14} style={{ color: 'rgb(var(--lm-red))' }} />;
      case 'baisse': return <ArrowDown size={14} style={{ color: 'rgb(var(--lm-green))' }} />;
      default: return <Check size={14} className="text-[rgb(var(--lm-text-muted))]" />;
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
      <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="patron-fixes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Charges fixes</p>
          <h1 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Wrench size={20} style={{ color: 'rgb(var(--lm-gold))' }} />
            {formatCurrency(totalFixes)}<span className="text-sm text-[rgb(var(--lm-text-muted))] font-normal">/mois</span>
          </h1>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
            onChange={e => { handleUploadReleve(e.target.files[0]); e.target.value = ''; }} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))] border border-[rgb(var(--lm-green)/0.2)] hover:bg-[rgb(var(--lm-green)/0.15)]"
            data-testid="upload-releve-btn">
            {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
            {uploading ? 'Analyse...' : 'Deposer un releve'}
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))] hover:opacity-90" data-testid="add-cost-btn">
                <Plus size={16} className="mr-2" /> Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))]">
              <DialogHeader>
                <DialogTitle className="font-title text-lg">Nouvelle charge</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Categorie</label>
                  <Input value={newCost.categorie}
                    onChange={e => setNewCost({ ...newCost, categorie: e.target.value })}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Ex: Loyer, Assurances..."
                    list="categories-list" data-testid="new-cost-cat" />
                  <datalist id="categories-list">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Libelle</label>
                  <Input value={newCost.libelle}
                    onChange={e => setNewCost({ ...newCost, libelle: e.target.value })}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" placeholder="Ex: AXA assurance..." data-testid="new-cost-label" />
                </div>
                <div>
                  <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-2">Montant mensuel</label>
                  <Input type="number" value={newCost.montant || ''}
                    onChange={e => setNewCost({ ...newCost, montant: parseFloat(e.target.value) || 0 })}
                    className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] font-mono" data-testid="new-cost-amount" />
                </div>
                <Button onClick={handleAdd} className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))]" data-testid="confirm-add-cost">
                  Ajouter
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Releve Analysis Results */}
      {releveData && (
        <div className="lm-card p-5 animate-slide-up" style={{ borderColor: 'rgb(var(--lm-green) / 0.3)' }} data-testid="releve-results">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText size={16} style={{ color: 'rgb(var(--lm-green))' }} />
              <div>
                <p className="text-sm text-[rgb(var(--lm-text))] font-medium">Releve analyse — {releveData.periode}</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{releveData.banque}</p>
              </div>
            </div>
            <button onClick={() => { setReleveData(null); setSelections({}); }} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
              <X size={16} />
            </button>
          </div>

          <div className="flex gap-3 flex-wrap mb-4">
            {releveData.nb_nouvelles > 0 && (
              <span className="text-[10px] bg-[rgb(var(--lm-green)/0.08)] text-[rgb(var(--lm-green))] px-2 py-1 rounded-sm">+{releveData.nb_nouvelles} nouvelle(s)</span>
            )}
            {releveData.nb_modifiees > 0 && (
              <span className="text-[10px] bg-[rgb(var(--lm-orange)/0.08)] text-[rgb(var(--lm-orange))] px-2 py-1 rounded-sm">{releveData.nb_modifiees} modifiee(s)</span>
            )}
            {releveData.nb_identiques > 0 && (
              <span className="text-[10px] bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] px-2 py-1 rounded-sm">{releveData.nb_identiques} identique(s)</span>
            )}
          </div>

          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
            {releveData.charges.map((charge, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-sm ${
                charge.status === 'nouveau' ? 'bg-[rgb(var(--lm-green)/0.04)]' :
                charge.status !== 'identique' ? 'bg-[rgb(var(--lm-orange)/0.04)]' : 'bg-[rgb(var(--lm-surface-elevated))]'
              }`} data-testid={`releve-charge-${i}`}>
                <div className="shrink-0">{statusIcon(charge.status)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[rgb(var(--lm-text))] truncate">{charge.libelle}</p>
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{charge.categorie}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm text-[rgb(var(--lm-text))]">{charge.montant.toFixed(2)} EUR</p>
                  {charge.status !== 'nouveau' && charge.status !== 'identique' && (
                    <p className={`text-[10px] font-mono ${charge.diff > 0 ? 'text-[rgb(var(--lm-red))]' : 'text-[rgb(var(--lm-green))]'}`}>
                      {charge.diff > 0 ? '+' : ''}{charge.diff.toFixed(2)} (etait {charge.ancien_montant.toFixed(2)})
                    </p>
                  )}
                </div>
                <div className="shrink-0">
                  <select value={selections[i] || 'skip'}
                    onChange={e => setSelections({ ...selections, [i]: e.target.value })}
                    className="bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-sm text-xs text-[rgb(var(--lm-text))] px-2 py-1.5"
                    data-testid={`releve-action-${i}`}>
                    {charge.status === 'nouveau' && <option value="add">Ajouter</option>}
                    {charge.existing_id && <option value="update">Mettre a jour</option>}
                    <option value="skip">Ignorer</option>
                  </select>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={handleAutoApplyReleve} disabled={applying}
              className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))] hover:opacity-90"
              data-testid="auto-apply-releve-btn">
              {applying ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Zap size={14} className="mr-2" />}
              Auto-appliquer + Telegram
            </Button>
            <Button onClick={handleApplyReleve} disabled={applying} variant="outline"
              className="flex-1 border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-secondary))]"
              data-testid="apply-releve-btn">
              {applying ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
              Appliquer manuellement
            </Button>
          </div>
        </div>
      )}

      {/* Seuil de survie */}
      <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-orange) / 0.2)' }} data-testid="seuil-survie">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={14} style={{ color: 'rgb(var(--lm-orange))' }} />
          <span className="text-xs tracking-[0.15em] uppercase" style={{ color: 'rgb(var(--lm-orange))' }}>Seuil de survie</span>
        </div>
        <p className="font-mono text-2xl" style={{ color: 'rgb(var(--lm-orange))' }}>{formatCurrency(seuilSurvie)}</p>
        <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">CA HT minimum = Fixes + Salaires charges</p>
      </div>

      {/* Costs by category */}
      {categories.map((cat, catIdx) => {
        const catCosts = groupedCosts[cat];
        const catTotal = catCosts.reduce((sum, c) => sum + c.montant, 0);
        return (
          <div key={cat} className="lm-card p-5 animate-slide-up" style={{ animationDelay: `${120 + catIdx * 60}ms`, animationFillMode: 'backwards' }} data-testid={`cat-${cat}`}>
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs tracking-[0.15em] uppercase" style={{ color: 'rgb(var(--lm-gold))' }}>{cat}</span>
              <span className="font-mono text-sm text-[rgb(var(--lm-text-secondary))]">{formatCurrency(catTotal)}</span>
            </div>
            <div className="space-y-0.5">
              {catCosts.map(cost => (
                <div key={cost.id} className="flex justify-between items-center py-2.5 px-3 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] transition-colors"
                  data-testid={`cost-${cost.id}`}>
                  <span className="text-sm text-[rgb(var(--lm-text))]">{cost.libelle}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-[rgb(var(--lm-text))]">{formatCurrency(cost.montant)}</span>
                    <button onClick={() => handleDelete(cost.id)}
                      className="text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Total */}
      <div className="lm-card p-5 flex justify-between items-center animate-slide-up" style={{ borderColor: 'rgb(var(--lm-gold) / 0.2)' }} data-testid="total-fixes">
        <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Total Charges Fixes</span>
        <span className="font-mono text-2xl" style={{ color: 'rgb(var(--lm-gold))' }}>{formatCurrency(totalFixes)}</span>
      </div>
    </div>
  );
};

export default PatronFixes;
